// src/components/AGTFit/workout/WorkoutStats.js
import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  useTheme,
  Paper,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Chip,
  Button,
  Avatar,
  IconButton
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { 
  Calendar,
  Activity,
  BarChart2,
  Ruler,
  Dumbbell,
  ChevronRight,
  BarChart,
  PieChart,
  Award,
  TrendingUp,
  Clock,
  ArrowUpRight,
  ArrowDownRight,
  ChevronLeft,
  ChevronUp,
  ChevronDown,
  Plus
} from 'lucide-react';
import agtfitService from '../../service/agtfitService';
import { format, parseISO, subDays, startOfWeek, endOfWeek, startOfMonth, endOfMonth, eachDayOfInterval } from 'date-fns';

// Color principal
const FIT_ORANGE = '#FF6B00';

// Colores para modo wallpaper
const WALLPAPER_COLORS = {
  bg: 'rgba(128, 128, 128, 0.3)',
  bgLight: 'rgba(128, 128, 128, 0.25)',
  border: 'rgba(128, 128, 128, 0.3)',
  text: '#FFFFFF',
  textSecondary: 'rgba(255, 255, 255, 0.7)'
};

// Nuevo NavigationBar estilizado con ancho adaptable
const StyledTabs = styled(Box)(({ theme }) => ({
  minHeight: '40px',
  height: '40px',
  background: theme.palette.actualMode === 'wallpaper'
    ? WALLPAPER_COLORS.bg
    : (theme.palette.mode === 'dark'
      ? 'rgba(40, 40, 42, 0.94)'
      : 'rgba(242, 242, 247, 1)'),
  backdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(10px)' : 'none',
  WebkitBackdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(10px)' : 'none',
  border: theme.palette.actualMode === 'wallpaper' ? `1px solid ${WALLPAPER_COLORS.border}` : 'none',
  padding: '6px',
  borderRadius: '24px',
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  maxWidth: '350px',
  margin: '0 auto',
  marginBottom: theme.spacing(3),
  marginTop: theme.spacing(3),
}));

// TabIndicator modificado para usar transform
const TabIndicator = styled('div')(({ theme }) => ({
  position: 'absolute',
  height: '28px',
  borderRadius: '24px',
  backgroundColor: theme.palette.actualMode === 'wallpaper'
    ? WALLPAPER_COLORS.bgLight
    : (theme.palette.mode === 'dark'
      ? 'rgba(118, 118, 128, 0.24)'
      : '#FFFFFF'),
  backdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(8px)' : 'none',
  WebkitBackdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(8px)' : 'none',
  border: theme.palette.actualMode === 'wallpaper' ? `1px solid ${WALLPAPER_COLORS.border}` : 'none',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  zIndex: 0,
  boxShadow: theme.palette.actualMode === 'wallpaper'
    ? 'none'
    : (theme.palette.mode === 'dark'
      ? 'none'
      : '0 2px 8px rgba(0, 0, 0, 0.04)'),
}));

// StyledTab con ancho adaptable al contenido
const StyledTab = styled(Box)(({ theme, selected }) => ({
  minHeight: '28px',
  height: '28px',
  minWidth: 'auto',
  padding: '0 16px',
  borderRadius: '24px',
  color:
    theme.palette.mode === 'dark'
      ? 'rgba(255, 255, 255, 0.7)'
      : '#666666',
  fontSize: '14px',
  fontWeight: 500,
  textTransform: 'none',
  zIndex: 1,
  transition: 'all 0.2s',
  backgroundColor: 'transparent',
  whiteSpace: 'nowrap',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  ...(selected && {
    backgroundImage: 'linear-gradient(90deg, #FF9800 0%, #FF6B00 100%)',
    backgroundClip: 'text',
    textFillColor: 'transparent',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
  }),
  '&:hover': {
    backgroundImage: 'linear-gradient(90deg, #FF9800 0%, #FF6B00 100%)',
    backgroundClip: 'text',
    textFillColor: 'transparent',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundColor: 'transparent',
  },
}));

// Tarjeta para las categorías
const CategoryCard = styled(Paper, {
  shouldForwardProp: (prop) => prop !== 'active'
})(({ theme, active }) => ({
  padding: theme.spacing(2),
  borderRadius: 16,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  height: '100%',
  backgroundColor: theme.palette.actualMode === 'wallpaper'
    ? (active ? 'rgba(255, 107, 0, 0.25)' : WALLPAPER_COLORS.bgLight)
    : (active
      ? theme.palette.mode === 'dark'
        ? 'rgba(255, 107, 0, 0.2)'
        : 'rgba(255, 107, 0, 0.1)'
      : theme.palette.mode === 'dark'
        ? '#1A1A1A'
        : '#FFFFFF'),
  backdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(8px)' : 'none',
  WebkitBackdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(8px)' : 'none',
  border: `1px solid ${active ? FIT_ORANGE : theme.palette.actualMode === 'wallpaper' ? WALLPAPER_COLORS.border : theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.03)'}`,
  boxShadow: theme.palette.actualMode === 'wallpaper' ? 'none' : undefined,
  transition: 'all 0.2s',
  '&:hover': {
    backgroundColor: theme.palette.actualMode === 'wallpaper'
      ? 'rgba(255, 107, 0, 0.2)'
      : (theme.palette.mode === 'dark'
        ? 'rgba(255, 107, 0, 0.15)'
        : 'rgba(255, 107, 0, 0.05)')
  }
}));

const IconBox = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'active'
})(({ theme, active }) => ({
  width: 50,
  height: 50,
  borderRadius: '50%',
  backgroundColor: active
    ? 'transparent'
    : theme.palette.mode === 'dark'
      ? 'rgba(255, 255, 255, 0.1)'
      : 'rgba(0, 0, 0, 0.05)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: theme.spacing(1),
  color: active ? '#FFFFFF' : theme.palette.text.primary,
  transition: 'all 0.2s',
  backgroundImage: active ? 'linear-gradient(90deg, #FF9800 0%, #FF6B00 100%)' : 'none',
}));

// Gráfico de barras con barras delgadas y redondeadas
const BarGraph = styled(Box)(({ theme }) => ({
  width: '100%',
  height: 220,
  backgroundColor: theme.palette.actualMode === 'wallpaper'
    ? WALLPAPER_COLORS.bg
    : (theme.palette.mode === 'dark' ? '#1A1A1A' : '#FFFFFF'),
  backdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(10px)' : 'none',
  WebkitBackdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(10px)' : 'none',
  borderRadius: 16,
  padding: theme.spacing(2, 2, 4, 2), // Más padding en la parte inferior
  display: 'flex',
  alignItems: 'flex-end',
  justifyContent: 'space-between',
  marginBottom: theme.spacing(2),
  border: `1px solid ${theme.palette.actualMode === 'wallpaper' ? WALLPAPER_COLORS.border : theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.03)'}`,
  boxShadow: theme.palette.actualMode === 'wallpaper' ? 'none' : undefined,
  position: 'relative',
}));

// Agrupar músculos en categorías más grandes
const groupMusclesByCategory = (exercises) => {
  const muscleGroups = {
    'Chest': ['CHEST'],
    'Back': ['UPPER BACK', 'LOWER BACK', 'LATS', 'TRAPS'],
    'Legs': ['QUADRICEPS', 'HAMSTRINGS', 'CALVES', 'GLUTES', 'ADDUCTORS', 'ABDUCTORS'],
    'Shoulders': ['SHOULDERS'],
    'Arms': ['BICEPS', 'TRICEPS', 'FOREARMS'],
    'Core': ['ABDOMINALS'],
    'Other': ['FULL BODY']
  };

  // Inicializar contadores
  const muscleData = {
    'Chest': { sets: 0, percentage: 0 },
    'Back': { sets: 0, percentage: 0 },
    'Legs': { sets: 0, percentage: 0 },
    'Shoulders': { sets: 0, percentage: 0 },
    'Arms': { sets: 0, percentage: 0 },
    'Core': { sets: 0, percentage: 0 },
    'Other': { sets: 0, percentage: 0 }
  };

  // Contar sets por grupo muscular
  exercises.forEach(exercise => {
    let counted = false;
    
    // Buscar a qué grupo pertenece este músculo
    for (const [group, muscles] of Object.entries(muscleGroups)) {
      if (muscles.includes(exercise.exerciseType)) {
        muscleData[group].sets += parseInt(exercise.sets || 0);
        counted = true;
        break;
      }
    }
    
    // Si no se encontró en ningún grupo, contar como "Other"
    if (!counted && exercise.sets) {
      muscleData['Other'].sets += parseInt(exercise.sets || 0);
    }
  });

  // Calcular el total de sets
  const totalSets = Object.values(muscleData).reduce((sum, group) => sum + group.sets, 0);
  
  // Calcular porcentajes y formatear datos
  const result = Object.entries(muscleData)
    .map(([muscle, data]) => {
      const percentage = totalSets > 0 ? Math.round((data.sets / totalSets) * 100) : 0;
      return { muscle, sets: data.sets, percentage };
    })
    .filter(item => item.sets > 0) // Eliminar grupos sin sets
    .sort((a, b) => b.sets - a.sets); // Ordenar por número de sets (descendente)

  return result;
};

// Obtener ejercicios principales basados en el número de sets
const getMainExercises = (exercises) => {
  // Agrupar por nombre de ejercicio
  const exerciseMap = {};
  
  exercises.forEach(exercise => {
    const name = exercise.name;
    if (!exerciseMap[name]) {
      exerciseMap[name] = {
        name,
        sets: 0,
        reps: 0,
        weight: 0,
        count: 0,
        maxWeight: 0
      };
    }
    
    exerciseMap[name].sets += parseInt(exercise.sets || 0);
    exerciseMap[name].reps += parseInt(exercise.reps || 0);
    exerciseMap[name].weight += parseFloat(exercise.weight || 0);
    exerciseMap[name].count += 1;
    exerciseMap[name].maxWeight = Math.max(exerciseMap[name].maxWeight, parseFloat(exercise.weight || 0));
  });
  
  // Convertir a array y calcular promedios
  const exerciseArray = Object.values(exerciseMap).map(exercise => {
    const avgWeight = exercise.count > 0 ? Math.round(exercise.weight / exercise.count) : 0;
    const avgReps = exercise.count > 0 ? Math.round(exercise.reps / exercise.count) : 0;
    
    return {
      ...exercise,
      avgWeight,
      avgReps
    };
  });
  
  // Ordenar por número total de sets (descendente)
  exerciseArray.sort((a, b) => b.sets - a.sets);
  
  // Calcular el total de sets para los porcentajes
  const totalSets = exerciseArray.reduce((sum, ex) => sum + ex.sets, 0);
  
  // Calcular porcentajes y devolver los 5 principales ejercicios
  return exerciseArray
    .slice(0, 5)
    .map((exercise, index) => ({
      name: exercise.name,
      sets: exercise.sets,
      percentage: totalSets > 0 ? Math.round((exercise.sets / totalSets) * 100) : 0,
      rank: index + 1
    }));
};

// Componente StatisticsContent
const StatisticsContent = ({ workoutData }) => {
  const theme = useTheme();
  const [muscleData, setMuscleData] = useState([]);
  const [currentWeekData, setCurrentWeekData] = useState([]);
  const [previousWeekData, setPreviousWeekData] = useState([]);
  const [mainExercises, setMainExercises] = useState([]);
  const [leaderboardExercises, setLeaderboardExercises] = useState([]);
  const [monthlyStats, setMonthlyStats] = useState({
    workouts: 0,
    totalTime: '0h',
    totalSets: 0,
    changePercent: 0
  });
  
  useEffect(() => {
    if (workoutData && workoutData.length > 0) {
      // Procesar datos para Set Count per Muscle Group
      const muscleGroups = groupMusclesByCategory(workoutData);
      setMuscleData(muscleGroups);
      
      // Separar datos de la semana actual y la anterior
      const today = new Date();
      const startOfCurrentWeek = startOfWeek(today, { weekStartsOn: 1 });
      const endOfCurrentWeek = endOfWeek(today, { weekStartsOn: 1 });
      const startOfPreviousWeek = subDays(startOfCurrentWeek, 7);
      const endOfPreviousWeek = subDays(endOfCurrentWeek, 7);
      
      // Filtrar ejercicios por semana
      const currentWeekExercises = workoutData.filter(ex => {
        const exDate = new Date(ex.exerciseDate);
        return exDate >= startOfCurrentWeek && exDate <= endOfCurrentWeek;
      });
      
      const previousWeekExercises = workoutData.filter(ex => {
        const exDate = new Date(ex.exerciseDate);
        return exDate >= startOfPreviousWeek && exDate <= endOfPreviousWeek;
      });
      
      // Procesar datos para cada semana
      const currentWeekMuscleGroups = groupMusclesByCategory(currentWeekExercises);
      const previousWeekMuscleGroups = groupMusclesByCategory(previousWeekExercises);
      
      setCurrentWeekData(currentWeekMuscleGroups);
      setPreviousWeekData(previousWeekMuscleGroups);
      
      // Procesar datos para Main Exercises
      const mainExercisesData = getMainExercises(workoutData);
      setMainExercises(mainExercisesData);
      
      // Procesar datos para Leaderboard Exercises (los 3 principales con más peso)
      const leaderboard = [...mainExercisesData]
        .sort((a, b) => {
          const exerciseA = workoutData.find(ex => ex.name === a.name) || {};
          const exerciseB = workoutData.find(ex => ex.name === b.name) || {};
          return (parseFloat(exerciseB.weight) || 0) - (parseFloat(exerciseA.weight) || 0);
        })
        .slice(0, 3)
        .map((ex, index) => {
          const exercise = workoutData.find(e => e.name === ex.name) || {};
          return {
            name: ex.name,
            best: `${exercise.weight || 0}kg × ${exercise.reps || 0}`,
            rank: index + 1
          };
        });
      
      setLeaderboardExercises(leaderboard);
      
      // Calcular estadísticas mensuales
      const uniqueDates = [...new Set(workoutData.map(ex => ex.exerciseDate))];
      const totalWorkouts = uniqueDates.length;
      
      const totalDuration = workoutData.reduce((sum, ex) => sum + (parseInt(ex.duration) || 0), 0);
      const totalHours = Math.floor(totalDuration / 60);
      const totalMinutes = totalDuration % 60;
      const formattedTime = totalHours > 0 
        ? `${totalHours}h${totalMinutes > 0 ? ` ${totalMinutes}m` : ''}`
        : `${totalMinutes}m`;
      
      const totalSets = workoutData.reduce((sum, ex) => sum + (parseInt(ex.sets) || 0), 0);
      
      // Simulamos un cambio porcentual (en una implementación real, esto compararía con el mes anterior)
      const changePercent = 5;
      
      setMonthlyStats({
        workouts: totalWorkouts,
        totalTime: formattedTime,
        totalSets: totalSets,
        changePercent: changePercent
      });
    }
  }, [workoutData]);

  return (
    <Box sx={{ mt: 3, mb: 4 }}>
      {/* Sección 1: Set count per muscle group */}
      <Paper elevation={0} sx={{
        p: 2, mb: 3, borderRadius: 2,
        backgroundColor: theme.palette.actualMode === 'wallpaper'
          ? WALLPAPER_COLORS.bg
          : 'transparent',
        backdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(10px)' : 'none',
        WebkitBackdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(10px)' : 'none',
        border: `1px solid ${theme.palette.actualMode === 'wallpaper'
          ? WALLPAPER_COLORS.border
          : theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
        boxShadow: theme.palette.actualMode === 'wallpaper' ? 'none' : undefined
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <BarChart size={18} style={{ marginRight: 8, color: FIT_ORANGE }} />
          <Typography variant="h6" fontWeight={600}>
            Set Count per Muscle Group
          </Typography>
        </Box>
        <Box sx={{ mt: 2 }}>
          {muscleData.length > 0 ? (
            muscleData.map((item, index) => (
              <Box key={index} sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="body2" fontWeight={500}>{item.muscle}</Typography>
                  <Typography variant="body2" color="text.secondary">{item.sets} sets ({item.percentage}%)</Typography>
                </Box>
                <Box sx={{ width: '100%', height: 8, bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)', borderRadius: 4, position: 'relative' }}>
                  <Box 
                    sx={{ 
                      width: `${item.percentage}%`, 
                      height: '100%', 
                      borderRadius: 4,
                      position: 'absolute',
                      left: 0,
                      top: 0,
                      background: 'linear-gradient(90deg, #FF9800 0%, #FF6B00 100%)'
                    }} 
                  />
                </Box>
              </Box>
            ))
          ) : (
            <Typography align="center" color="text.secondary">No workout data available</Typography>
          )}
        </Box>
      </Paper>
      
      {/* Sección 2: Muscle distribution (Chart) */}
      <Paper elevation={0} sx={{
        p: 2, mb: 3, borderRadius: 2,
        backgroundColor: theme.palette.actualMode === 'wallpaper'
          ? WALLPAPER_COLORS.bg
          : 'transparent',
        backdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(10px)' : 'none',
        WebkitBackdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(10px)' : 'none',
        border: `1px solid ${theme.palette.actualMode === 'wallpaper'
          ? WALLPAPER_COLORS.border
          : theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
        boxShadow: theme.palette.actualMode === 'wallpaper' ? 'none' : undefined
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <PieChart size={18} style={{ marginRight: 8, color: FIT_ORANGE }} />
          <Typography variant="h6" fontWeight={600}>
            Muscle Distribution (Chart)
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
          <Box sx={{ width: '48%' }}>
            <Typography variant="subtitle2" align="center" gutterBottom>Current Week</Typography>
            <Box sx={{ height: 150, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              {currentWeekData.length > 0 ? (
                <Box sx={{ 
                  width: 150, 
                  height: 150, 
                  borderRadius: '50%', 
                  background: generateConicGradient(currentWeekData)
                }} />
              ) : (
                <Typography align="center" color="text.secondary">No data available</Typography>
              )}
            </Box>
          </Box>
          <Box sx={{ width: '48%' }}>
            <Typography variant="subtitle2" align="center" gutterBottom>Previous Week</Typography>
            <Box sx={{ height: 150, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              {previousWeekData.length > 0 ? (
                <Box sx={{ 
                  width: 150, 
                  height: 150, 
                  borderRadius: '50%', 
                  background: generateConicGradient(previousWeekData)
                }} />
              ) : (
                <Typography align="center" color="text.secondary">No data available</Typography>
              )}
            </Box>
          </Box>
        </Box>
        <Box sx={{ mt: 2 }}>
          <Grid container spacing={1}>
            {muscleData.slice(0, 6).map((item, index) => (
              <Grid item xs={6} key={index}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box 
                    sx={{ 
                      width: 12, 
                      height: 12, 
                      borderRadius: '50%', 
                      bgcolor: getColorForIndex(index),
                      mr: 1 
                    }} 
                  />
                  <Typography variant="caption">{item.muscle}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Paper>
      
      {/* Sección 3: Main Exercises */}
      <Paper elevation={0} sx={{
        p: 2, mb: 3, borderRadius: 2,
        backgroundColor: theme.palette.actualMode === 'wallpaper'
          ? WALLPAPER_COLORS.bg
          : 'transparent',
        backdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(10px)' : 'none',
        WebkitBackdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(10px)' : 'none',
        border: `1px solid ${theme.palette.actualMode === 'wallpaper'
          ? WALLPAPER_COLORS.border
          : theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
        boxShadow: theme.palette.actualMode === 'wallpaper' ? 'none' : undefined
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Dumbbell size={18} style={{ marginRight: 8, color: FIT_ORANGE }} />
          <Typography variant="h6" fontWeight={600}>
            Main Exercises
          </Typography>
        </Box>
        {mainExercises.length > 0 ? (
          <List>
            {mainExercises.map((exercise, index) => (
              <ListItem 
                key={index} 
                divider={index < mainExercises.length - 1}
                sx={{ px: 1 }}
              >
                <ListItemText 
                  primary={exercise.name} 
                  secondary={`${exercise.sets} sets this month`} 
                />
                <Typography variant="body2" color="text.secondary">
                  {exercise.percentage}% of total
                </Typography>
              </ListItem>
            ))}
          </List>
        ) : (
          <Typography align="center" color="text.secondary" sx={{ py: 2 }}>No exercise data available</Typography>
        )}
      </Paper>
      
      {/* Sección 4: Leaderboard Exercises */}
      <Paper elevation={0} sx={{
        p: 2, mb: 3, borderRadius: 2,
        backgroundColor: theme.palette.actualMode === 'wallpaper'
          ? WALLPAPER_COLORS.bg
          : 'transparent',
        backdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(10px)' : 'none',
        WebkitBackdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(10px)' : 'none',
        border: `1px solid ${theme.palette.actualMode === 'wallpaper'
          ? WALLPAPER_COLORS.border
          : theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
        boxShadow: theme.palette.actualMode === 'wallpaper' ? 'none' : undefined
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Award size={18} style={{ marginRight: 8, color: FIT_ORANGE }} />
          <Typography variant="h6" fontWeight={600}>
            Leaderboard Exercises
          </Typography>
        </Box>
        {leaderboardExercises.length > 0 ? (
          <List>
            {leaderboardExercises.map((exercise, index) => (
              <ListItem 
                key={index} 
                divider={index < leaderboardExercises.length - 1}
                sx={{ px: 1 }}
              >
                <ListItemText 
                  primary={exercise.name} 
                  secondary={`Best: ${exercise.best}`} 
                />
                <Chip 
                  label={`Rank #${exercise.rank}`} 
                  size="small" 
                  sx={{ 
                    background: exercise.rank === 1 ? 'linear-gradient(90deg, #FF9800 0%, #FF6B00 100%)' : 'default',
                    color: exercise.rank === 1 ? 'white' : 'default'
                  }}
                />
              </ListItem>
            ))}
          </List>
        ) : (
          <Typography align="center" color="text.secondary" sx={{ py: 2 }}>No exercise data available</Typography>
        )}
      </Paper>
      
      {/* Sección 5: Monthly Report */}
      <Paper elevation={0} sx={{
        p: 2, mb: 3, borderRadius: 2,
        backgroundColor: theme.palette.actualMode === 'wallpaper'
          ? WALLPAPER_COLORS.bg
          : 'transparent',
        backdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(10px)' : 'none',
        WebkitBackdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(10px)' : 'none',
        border: `1px solid ${theme.palette.actualMode === 'wallpaper'
          ? WALLPAPER_COLORS.border
          : theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
        boxShadow: theme.palette.actualMode === 'wallpaper' ? 'none' : undefined
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <TrendingUp size={18} style={{ marginRight: 8, color: FIT_ORANGE }} />
          <Typography variant="h6" fontWeight={600}>
            Monthly Report
          </Typography>
        </Box>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={6} sm={3}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography 
                variant="h4" 
                fontWeight={600} 
                sx={{
                  backgroundImage: 'linear-gradient(90deg, #FF9800 0%, #FF6B00 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                {monthlyStats.workouts}
              </Typography>
              <Typography variant="body2">Workouts</Typography>
            </Box>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography 
                variant="h4" 
                fontWeight={600} 
                sx={{
                  backgroundImage: 'linear-gradient(90deg, #FF9800 0%, #FF6B00 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                {monthlyStats.totalTime}
              </Typography>
              <Typography variant="body2">Total Time</Typography>
            </Box>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography 
                variant="h4" 
                fontWeight={600} 
                sx={{
                  backgroundImage: 'linear-gradient(90deg, #FF9800 0%, #FF6B00 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                {monthlyStats.totalSets}
              </Typography>
              <Typography variant="body2">Total Sets</Typography>
            </Box>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography 
                variant="h4" 
                fontWeight={600} 
                sx={{
                  backgroundImage: 'linear-gradient(90deg, #FF9800 0%, #FF6B00 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                {monthlyStats.changePercent >= 0 ? '+' : ''}{monthlyStats.changePercent}%
              </Typography>
              <Typography variant="body2">vs Last Month</Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

// Función para generar gradiente cónico para el gráfico circular
const generateConicGradient = (muscleData) => {
  if (!muscleData || muscleData.length === 0) return '';
  
  let gradientString = 'conic-gradient(';
  let startPercentage = 0;
  
  muscleData.forEach((item, index) => {
    const endPercentage = startPercentage + item.percentage;
    const color = getColorForIndex(index);
    
    gradientString += `${color} ${startPercentage}% ${endPercentage}%${index < muscleData.length - 1 ? ',' : ''}`;
    startPercentage = endPercentage;
  });
  
  gradientString += ')';
  return gradientString;
};

// Función para obtener color según índice
const getColorForIndex = (index) => {
  const colors = [
    FIT_ORANGE,
    '#FF9800',
    '#FFB74D',
    '#FFCC80',
    '#FFE0B2',
    '#FFECB3'
  ];
  
  return colors[index % colors.length];
};

// Componente ExercisesContent
const ExercisesContent = () => {
  const theme = useTheme();
  const [activeCategory, setActiveCategory] = useState(null);
  const [exerciseCategories] = useState([
    { name: 'Strength', icon: <Dumbbell size={20} /> },
    { name: 'Cardio', icon: <Activity size={20} /> },
    { name: 'Flexibility', icon: <Activity size={20} /> },
    { name: 'Recovery', icon: <Clock size={20} /> },
  ]);
  const [loading, setLoading] = useState(true);
  const [recentExercises, setRecentExercises] = useState([]);

  const handleCategoryClick = (index) => {
    setActiveCategory(index === activeCategory ? null : index);
  };

  // Cargar ejercicios recientes
  useEffect(() => {
    const fetchRecentExercises = async () => {
      setLoading(true);
      try {
        // Obtener la fecha actual
        const today = new Date();
        const formattedDate = format(today, 'yyyy-MM-dd');
        
        // Obtener ejercicios del día actual
        const response = await agtfitService.getExercises(formattedDate);
        
        if (response.success && response.data) {
          // Formatear los datos para mostrar
          const formattedExercises = response.data.map(exercise => ({
            name: exercise.name,
            muscle: exercise.exerciseType,
            sets: exercise.sets,
            reps: exercise.reps,
            weight: exercise.weight
          }));
          
          setRecentExercises(formattedExercises.slice(0, 3)); // Mostrar solo los 3 más recientes
        } else {
          console.error("Error loading recent exercises:", response.error);
          setRecentExercises([]);
        }
      } catch (error) {
        console.error("Error in fetchRecentExercises:", error);
        setRecentExercises([]);
      } finally {
        setLoading(false);
      }
    };
    
    fetchRecentExercises();
  }, []);

  return (
    <Box sx={{ mt: 3, mb: 4 }}>
      {/* Categorías de ejercicios */}
      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
        Exercise Categories
      </Typography>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {exerciseCategories.map((category, index) => (
          <Grid item xs={3} key={index}>
            <Box 
              sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center',
                cursor: 'pointer'
              }}
              onClick={() => handleCategoryClick(index)}
            >
              <Avatar 
                sx={{ 
                  width: 56, 
                  height: 56, 
                  mb: 1,
                  ...(activeCategory === index ? {
                    background: 'linear-gradient(90deg, #FF9800 0%, #FF6B00 100%)',
                    color: 'white'
                  } : {
                    bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
                    color: theme.palette.text.primary
                  })
                }}
              >
                {category.icon}
              </Avatar>
              <Typography variant="body2" align="center">
                {category.name}
              </Typography>
            </Box>
          </Grid>
        ))}
      </Grid>

      {/* Ejercicios recientes */}
      <Paper elevation={0} sx={{
        p: 2, mb: 3, borderRadius: 2,
        backgroundColor: theme.palette.actualMode === 'wallpaper'
          ? WALLPAPER_COLORS.bg
          : 'transparent',
        backdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(10px)' : 'none',
        WebkitBackdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(10px)' : 'none',
        border: `1px solid ${theme.palette.actualMode === 'wallpaper'
          ? WALLPAPER_COLORS.border
          : theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
        boxShadow: theme.palette.actualMode === 'wallpaper' ? 'none' : undefined
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h6" fontWeight={600}>
            Recent Exercises
          </Typography>
        </Box>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={30} sx={{ color: FIT_ORANGE }} />
          </Box>
        ) : recentExercises.length > 0 ? (
          <List>
            {recentExercises.map((exercise, index) => (
              <ListItem 
                key={index} 
                divider={index < recentExercises.length - 1}
                sx={{ px: 1 }}
                secondaryAction={
                  <IconButton edge="end">
                    <ChevronRight size={16} />
                  </IconButton>
                }
              >
                <ListItemText 
                  primary={exercise.name} 
                  secondary={`${exercise.muscle} • ${exercise.sets} × ${exercise.reps} ${exercise.weight > 0 ? `• ${exercise.weight}kg` : ''}`} 
                />
              </ListItem>
            ))}
          </List>
        ) : (
          <Typography align="center" color="text.secondary" sx={{ py: 2 }}>
            No recent exercises found
          </Typography>
        )}
      </Paper>
    </Box>
  );
};

// Componente MeasuresContent
const MeasuresContent = () => {
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [weightData, setWeightData] = useState([]);
  const [bodyMeasurements, setBodyMeasurements] = useState({
    chest: { current: 0, previous: 0 },
    waist: { current: 0, previous: 0 },
    hips: { current: 0, previous: 0 },
    biceps: { current: 0, previous: 0 },
    thighs: { current: 0, previous: 0 }
  });
  const [bmi, setBmi] = useState({ value: 0, category: 'Normal weight' });
  const [weightChange, setWeightChange] = useState({ amount: 0, percentage: 0 });

  // Cargar datos de check-ins
  useEffect(() => {
    const fetchCheckIns = async () => {
      setLoading(true);
      try {
        const response = await agtfitService.getCheckIns();
        
        if (response.success && response.data && response.data.length > 0) {
          // Ordenar por fecha (más reciente primero)
          const sortedCheckIns = [...response.data].sort((a, b) => 
            new Date(b.checkInDate) - new Date(a.checkInDate)
          );
          
          // Extraer datos de peso
          const weightHistory = sortedCheckIns
            .filter(checkIn => checkIn.weight)
            .map(checkIn => ({
              date: checkIn.checkInDate,
              value: parseFloat(checkIn.weight)
            }))
            .slice(0, 5) // Limitar a los últimos 5 registros
            .reverse(); // Ordenar cronológicamente
          
          setWeightData(weightHistory);
          
          // Calcular cambios en el peso
          if (weightHistory.length >= 2) {
            const firstWeight = weightHistory[0].value;
            const lastWeight = weightHistory[weightHistory.length - 1].value;
            const weightDiff = (lastWeight - firstWeight).toFixed(1);
            const weightPercentage = ((weightDiff / firstWeight) * 100).toFixed(1);
            
            setWeightChange({
              amount: weightDiff,
              percentage: weightPercentage
            });
          }
          
          // Extraer medidas corporales
          // Obtener el check-in más reciente y el anterior para comparar
          const latestCheckIn = sortedCheckIns[0];
          const previousCheckIn = sortedCheckIns.length > 1 ? sortedCheckIns[1] : null;
          
          if (latestCheckIn && latestCheckIn.measurements) {
            const measurements = latestCheckIn.measurements;
            const prevMeasurements = previousCheckIn ? previousCheckIn.measurements : {};
            
            // Función para extraer valor numérico de una medida
            const extractMeasurement = (obj, key) => {
              if (!obj || !obj[key]) return 0;
              // Si es un objeto DynamoDB (con propiedad S), extraer el valor
              if (obj[key].S) return parseInt(obj[key].S);
              // Si es un valor directo
              return parseInt(obj[key]);
            };
            
            setBodyMeasurements({
              chest: { 
                current: extractMeasurement(measurements, 'chest'), 
                previous: extractMeasurement(prevMeasurements, 'chest') 
              },
              waist: { 
                current: extractMeasurement(measurements, 'waist'), 
                previous: extractMeasurement(prevMeasurements, 'waist') 
              },
              hips: { 
                current: extractMeasurement(measurements, 'hip'), 
                previous: extractMeasurement(prevMeasurements, 'hip') 
              },
              biceps: { 
                current: extractMeasurement(measurements, 'arm'), 
                previous: extractMeasurement(prevMeasurements, 'arm') 
              },
              thighs: { 
                current: extractMeasurement(measurements, 'thigh'), 
                previous: extractMeasurement(prevMeasurements, 'thigh') 
              }
            });
            
            // Calcular IMC si tenemos altura en el perfil
            const fetchProfile = async () => {
              const profileResponse = await agtfitService.getProfile();
              if (profileResponse.success && profileResponse.data) {
                const height = profileResponse.data.height;
                if (height && latestCheckIn.weight) {
                  // Altura en metros (si está en cm)
                  const heightInM = height > 3 ? height / 100 : height;
                  // Calcular IMC: peso(kg) / altura(m)²
                  const bmiValue = (latestCheckIn.weight / (heightInM * heightInM)).toFixed(1);
                  
                  // Determinar categoría de IMC
                  let category = '';
                  if (bmiValue < 18.5) category = 'Underweight';
                  else if (bmiValue < 25) category = 'Normal weight';
                  else if (bmiValue < 30) category = 'Overweight';
                  else category = 'Obesity';
                  
                  setBmi({ value: bmiValue, category });
                }
              }
            };
            
            fetchProfile();
          }
        }
      } catch (error) {
        console.error("Error fetching check-ins:", error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchCheckIns();
  }, []);

  return (
    <Box sx={{ mt: 3, mb: 4 }}>
      {/* Peso actual */}
      <Paper elevation={0} sx={{
        p: 2, mb: 3, borderRadius: 2,
        backgroundColor: theme.palette.actualMode === 'wallpaper'
          ? WALLPAPER_COLORS.bg
          : 'transparent',
        backdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(10px)' : 'none',
        WebkitBackdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(10px)' : 'none',
        border: `1px solid ${theme.palette.actualMode === 'wallpaper'
          ? WALLPAPER_COLORS.border
          : theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
        boxShadow: theme.palette.actualMode === 'wallpaper' ? 'none' : undefined
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="h6" fontWeight={600}>
            Current Weight
          </Typography>
          <Button 
            variant="text" 
            size="small" 
            endIcon={<ChevronRight size={16} />}
            sx={{ color: FIT_ORANGE }}
          >
            History
          </Button>
        </Box>
        
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={30} sx={{ color: FIT_ORANGE }} />
          </Box>
        ) : weightData.length > 0 ? (
          <>
            <Box sx={{ display: 'flex', alignItems: 'flex-end', mt: 2 }}>
              <Typography variant="h3" fontWeight={700} sx={{ mr: 2 }}>
                {weightData[weightData.length-1].value}
              </Typography>
              <Typography variant="h6" sx={{ mb: 0.5, opacity: 0.7 }}>
                kg
              </Typography>
              {weightChange.amount !== 0 && (
                <Box 
                  sx={{ 
                    ml: 2, 
                    display: 'flex', 
                    alignItems: 'center',
                    color: parseFloat(weightChange.amount) < 0 ? 'success.main' : 'error.main'
                  }}
                >
                  {parseFloat(weightChange.amount) < 0 ? (
                    <ArrowDownRight size={20} />
                  ) : (
                    <ArrowUpRight size={20} />
                  )}
                  <Typography variant="body2" fontWeight={500} sx={{ ml: 0.5 }}>
                    {weightChange.amount} kg ({weightChange.percentage}%)
                  </Typography>
                </Box>
              )}
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Last updated: {format(parseISO(weightData[weightData.length-1].date), 'MMM d, yyyy')}
            </Typography>
          </>
        ) : (
          <Typography align="center" color="text.secondary" sx={{ py: 3 }}>
            No weight data available
          </Typography>
        )}
      </Paper>

      {/* Medidas corporales */}
      <Paper elevation={0} sx={{
        p: 2, mb: 3, borderRadius: 2,
        backgroundColor: theme.palette.actualMode === 'wallpaper'
          ? WALLPAPER_COLORS.bg
          : 'transparent',
        backdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(10px)' : 'none',
        WebkitBackdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(10px)' : 'none',
        border: `1px solid ${theme.palette.actualMode === 'wallpaper'
          ? WALLPAPER_COLORS.border
          : theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
        boxShadow: theme.palette.actualMode === 'wallpaper' ? 'none' : undefined
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h6" fontWeight={600}>
            Body Measurements
          </Typography>
        </Box>
        
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={30} sx={{ color: FIT_ORANGE }} />
          </Box>
        ) : (
          <List>
            {Object.entries(bodyMeasurements).map(([part, data], index) => {
              const change = data.current - data.previous;
              return (
                <ListItem 
                  key={part} 
                  divider={index < Object.keys(bodyMeasurements).length - 1}
                  sx={{ px: 1 }}
                >
                  <ListItemText 
                    primary={part.charAt(0).toUpperCase() + part.slice(1)} 
                    secondary={weightData.length > 0 ? `Last updated: ${format(parseISO(weightData[weightData.length-1].date), 'MMM d, yyyy')}` : "Not measured"} 
                  />
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography variant="body1" fontWeight={500}>
                      {data.current > 0 ? `${data.current} cm` : 'N/A'}
                    </Typography>
                    {data.current > 0 && data.previous > 0 && (
                      <Box 
                        sx={{ 
                          display: 'flex', 
                          alignItems: 'center',
                          justifyContent: 'flex-end',
                          color: change > 0 ? 'success.main' : change < 0 ? 'error.main' : 'text.secondary'
                        }}
                      >
                        {change !== 0 && (
                          change > 0 ? <ChevronUp size={16} /> : <ChevronDown size={16} />
                        )}
                        <Typography variant="caption" sx={{ ml: 0.5 }}>
                          {change > 0 ? '+' : ''}{change} cm
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </ListItem>
              );
            })}
          </List>
        )}
      </Paper>

      {/* Calculadora de IMC */}
      <Paper elevation={0} sx={{
        p: 2, mb: 3, borderRadius: 2,
        backgroundColor: theme.palette.actualMode === 'wallpaper'
          ? WALLPAPER_COLORS.bg
          : 'transparent',
        backdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(10px)' : 'none',
        WebkitBackdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(10px)' : 'none',
        border: `1px solid ${theme.palette.actualMode === 'wallpaper'
          ? WALLPAPER_COLORS.border
          : theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
        boxShadow: theme.palette.actualMode === 'wallpaper' ? 'none' : undefined
      }}>
        <Typography variant="h6" fontWeight={600} gutterBottom>
          BMI Calculator
        </Typography>
        <Box sx={{ mt: 2, p: 2, bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)', borderRadius: 1 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={7}>
              <Typography variant="body2">Your BMI</Typography>
              <Typography variant="h5" fontWeight={600} sx={{ mt: 0.5 }}>
                {bmi.value > 0 ? bmi.value : 'N/A'}
              </Typography>
              {bmi.value > 0 && (
                <Chip 
                  label={bmi.category} 
                  size="small" 
                  sx={{ 
                    background: 'linear-gradient(90deg, #FF9800 0%, #FF6B00 100%)',
                    color: 'white',
                    mt: 1
                  }}
                />
              )}
            </Grid>
            <Grid item xs={5}>
              <Box sx={{ width: '100%', height: 8, bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)', borderRadius: 4, position: 'relative', mb: 1 }}>
                <Box 
                  sx={{ 
                    width: `${Math.min(Math.max((bmi.value - 18.5) * 100 / 11.5, 0), 100)}%`, 
                    height: '100%', 
                    background: 'linear-gradient(90deg, #FF9800 0%, #FF6B00 100%)',
                    borderRadius: 4,
                  }} 
                />
                <Box 
                  sx={{ 
                    position: 'absolute',
                    left: `${Math.min(Math.max((bmi.value - 18.5) * 100 / 11.5, 0), 100)}%`,
                    top: '50%',
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    bgcolor: FIT_ORANGE,
                    transform: 'translate(-50%, -50%)'
                  }}
                />
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                <Typography variant="caption" color="text.secondary">18.5</Typography>
                <Typography variant="caption" color="text.secondary">25</Typography>
                <Typography variant="caption" color="text.secondary">30</Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Paper>
    </Box>
  );
};

// Componente CalendarContent
const CalendarContent = () => {
  const theme = useTheme();
  const today = new Date();
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [workoutDates, setWorkoutDates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [calendarKey, setCalendarKey] = useState(0); // Key para forzar re-render completo
  
  // Generar días del mes actual
  const startDate = startOfMonth(currentMonth);
  const endDate = endOfMonth(currentMonth);
  const daysInMonth = eachDayOfInterval({ start: startDate, end: endDate });
  
  // Añadir días de la semana (cabecera)
  const weekDays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  
  // Próximos entrenamientos programados
  const [scheduledWorkouts] = useState([
    { date: '2025-05-17', name: 'Upper Body', time: '09:00 AM' },
    { date: '2025-05-19', name: 'Lower Body', time: '06:30 PM' },
    { date: '2025-05-22', name: 'Full Body', time: '07:00 AM' }
  ]);

  // Cargar datos de ejercicios para el mes actual
  useEffect(() => {
    const fetchWorkoutDates = async () => {
      setLoading(true);
      try {
        // Calcular startDate y endDate dentro del efecto
        const startOfCurrentMonth = startOfMonth(currentMonth);
        const endOfCurrentMonth = endOfMonth(currentMonth);
  
        const firstDay = format(startOfCurrentMonth, 'yyyy-MM-dd');
        const lastDay = format(endOfCurrentMonth, 'yyyy-MM-dd');
        
        // Obtener ejercicios para el rango de fechas
        const response = await agtfitService.getWorkoutStats(firstDay, lastDay);
        
        if (response.success && response.data) {
          // Extraer fechas únicas con ejercicios
          const dates = [...new Set(response.data.map(ex => ex.exerciseDate))];
          setWorkoutDates(dates);
        } else {
          console.error("Error loading workout dates:", response.error);
          setWorkoutDates([]);
        }
      } catch (error) {
        console.error("Error in fetchWorkoutDates:", error);
        setWorkoutDates([]);
      } finally {
        setLoading(false);
      }
    };
    
    fetchWorkoutDates();
  }, [currentMonth]);

  const handlePrevMonth = () => {
    setCurrentMonth(prevMonth => new Date(prevMonth.getFullYear(), prevMonth.getMonth() - 1));
    // Forzar re-render completo para evitar parpadeo
    setCalendarKey(prev => prev + 1);
  };

  const handleNextMonth = () => {
    setCurrentMonth(prevMonth => new Date(prevMonth.getFullYear(), prevMonth.getMonth() + 1));
    // Forzar re-render completo para evitar parpadeo
    setCalendarKey(prev => prev + 1);
  };

  // Calcular estadísticas de consistencia
  const consistencyStats = {
    monthlyWorkouts: workoutDates.length,
    currentStreak: calculateCurrentStreak(workoutDates, today)
  };

  return (
    <Box sx={{ mt: 3, mb: 4 }}>
      {/* Calendario mensual */}
      <Paper
        elevation={0}
        sx={{
          p: 2, mb: 3, borderRadius: 2,
          backgroundColor: theme.palette.actualMode === 'wallpaper'
            ? WALLPAPER_COLORS.bg
            : 'transparent',
          backdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(10px)' : 'none',
          WebkitBackdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(10px)' : 'none',
          border: `1px solid ${theme.palette.actualMode === 'wallpaper'
            ? WALLPAPER_COLORS.border
            : theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
          boxShadow: theme.palette.actualMode === 'wallpaper' ? 'none' : undefined
        }}
        key={calendarKey} // Usar key para forzar re-render completo
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h6" fontWeight={600}>
            Workout Calendar
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton size="small" onClick={handlePrevMonth}>
              <ChevronLeft size={20} />
            </IconButton>
            <Typography variant="subtitle1" sx={{ mx: 1 }}>
              {format(currentMonth, 'MMMM yyyy')}
            </Typography>
            <IconButton size="small" onClick={handleNextMonth}>
              <ChevronRight size={20} />
            </IconButton>
          </Box>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={30} sx={{ color: FIT_ORANGE }} />
          </Box>
        ) : (
          <>
            {/* Días de la semana */}
            <Grid container sx={{ mb: 1 }}>
              {weekDays.map(day => (
                <Grid item xs={12/7} key={day} sx={{ textAlign: 'center' }}>
                  <Typography variant="caption" color="text.secondary">
                    {day}
                  </Typography>
                </Grid>
              ))}
            </Grid>

            {/* Días del mes */}
            <Grid container>
              {/* Espacios vacíos para alinear con el día de la semana */}
              {Array.from({ length: startDate.getDay() === 0 ? 6 : startDate.getDay() - 1 }).map((_, index) => (
                <Grid item xs={12/7} key={`empty-${index}`} sx={{ height: 40 }} />
              ))}

              {/* Días del mes */}
              {daysInMonth.map(day => {
                const dateStr = format(day, 'yyyy-MM-dd');
                const isToday = format(today, 'yyyy-MM-dd') === dateStr;
                const hasWorkout = workoutDates.includes(dateStr);
                
                return (
                  <Grid item xs={12/7} key={dateStr}>
                    <Box 
                      sx={{ 
                        height: 40, 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        position: 'relative',
                        cursor: 'pointer',
                        '&:hover': {
                          bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)',
                          borderRadius: 1
                        }
                      }}
                    >
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          fontWeight: isToday ? 700 : 400,
                          color: isToday ? FIT_ORANGE : 'text.primary'
                        }}
                      >
                        {format(day, 'd')}
                      </Typography>
                      {hasWorkout && (
                        <Box 
                          sx={{ 
                            position: 'absolute',
                            bottom: 5,
                            width: 4,
                            height: 4,
                            borderRadius: '50%',
                            bgcolor: FIT_ORANGE
                          }}
                        />
                      )}
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          </>
        )}
      </Paper>

      {/* Próximos entrenamientos */}
      <Paper elevation={0} sx={{
        p: 2, mb: 3, borderRadius: 2,
        backgroundColor: theme.palette.actualMode === 'wallpaper'
          ? WALLPAPER_COLORS.bg
          : 'transparent',
        backdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(10px)' : 'none',
        WebkitBackdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(10px)' : 'none',
        border: `1px solid ${theme.palette.actualMode === 'wallpaper'
          ? WALLPAPER_COLORS.border
          : theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
        boxShadow: theme.palette.actualMode === 'wallpaper' ? 'none' : undefined
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h6" fontWeight={600}>
            Upcoming Workouts
          </Typography>
          <Button 
            variant="text" 
            size="small" 
            endIcon={<Plus size={16} />}
            sx={{ color: FIT_ORANGE }}
          >
            Schedule
          </Button>
        </Box>
        <List>
          {scheduledWorkouts.map((workout, index) => (
            <ListItem 
              key={index} 
              divider={index < scheduledWorkouts.length - 1}
              sx={{ px: 1 }}
            >
              <Box sx={{ mr: 2, textAlign: 'center', minWidth: 40 }}>
                <Typography variant="h6" fontWeight={600}>
                  {format(parseISO(workout.date), 'd')}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {format(parseISO(workout.date), 'MMM')}
                </Typography>
              </Box>
              <ListItemText 
                primary={workout.name} 
                secondary={workout.time} 
              />
              <Button 
                variant="outlined" 
                size="small"
                sx={{ 
                  borderColor: FIT_ORANGE, 
                  color: FIT_ORANGE,
                  '&:hover': { borderColor: '#E66000' }
                }}
              >
                View
              </Button>
            </ListItem>
          ))}
        </List>
      </Paper>

      {/* Estadísticas de consistencia */}
      <Paper elevation={0} sx={{
        p: 2, mb: 3, borderRadius: 2,
        backgroundColor: theme.palette.actualMode === 'wallpaper'
          ? WALLPAPER_COLORS.bg
          : 'transparent',
        backdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(10px)' : 'none',
        WebkitBackdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(10px)' : 'none',
        border: `1px solid ${theme.palette.actualMode === 'wallpaper'
          ? WALLPAPER_COLORS.border
          : theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
        boxShadow: theme.palette.actualMode === 'wallpaper' ? 'none' : undefined
      }}>
        <Typography variant="h6" fontWeight={600} gutterBottom>
          Workout Consistency
        </Typography>
        <Box sx={{ mt: 2 }}>
          <Grid container spacing={3}>
            <Grid item xs={6}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  This Month
                </Typography>
                <Box sx={{ position: 'relative', display: 'inline-block' }}>
                  <Box 
                    sx={{ 
                      width: 80, 
                      height: 80, 
                      borderRadius: '50%', 
                      border: `8px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)'}`,
                      position: 'relative'
                    }}
                  >
                    <Box 
                      sx={{ 
                        width: 80, 
                        height: 80, 
                        borderRadius: '50%', 
                        border: '8px solid transparent',
                        borderTopColor: FIT_ORANGE,
                        borderRightColor: FIT_ORANGE,
                        transform: 'rotate(45deg)',
                        position: 'absolute',
                        top: -8,
                        left: -8
                      }}
                    />
                    <Box 
                      sx={{ 
                        position: 'absolute', 
                        top: '50%', 
                        left: '50%', 
                        transform: 'translate(-50%, -50%)' 
                      }}
                    >
                      <Typography variant="h5" fontWeight={700}>
                        {consistencyStats.monthlyWorkouts}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Current Streak
                </Typography>
                <Box sx={{ position: 'relative', display: 'inline-block' }}>
                  <Box 
                    sx={{ 
                      width: 80, 
                      height: 80, 
                      borderRadius: '50%', 
                      border: `8px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)'}`,
                      position: 'relative'
                    }}
                  >
                    <Box 
                      sx={{ 
                        width: 80, 
                        height: 80, 
                        borderRadius: '50%', 
                        border: '8px solid transparent',
                        borderTopColor: FIT_ORANGE,
                        borderRightColor: FIT_ORANGE,
                        borderBottomColor: FIT_ORANGE,
                        transform: 'rotate(45deg)',
                        position: 'absolute',
                        top: -8,
                        left: -8
                      }}
                    />
                    <Box 
                      sx={{ 
                        position: 'absolute', 
                        top: '50%', 
                        left: '50%', 
                        transform: 'translate(-50%, -50%)' 
                      }}
                    >
                      <Typography variant="h5" fontWeight={700}>
                        {consistencyStats.currentStreak}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Grid>
          </Grid>
          <Typography variant="body2" align="center" sx={{ mt: 2 }}>
            You've worked out on <strong>{consistencyStats.monthlyWorkouts} days</strong> this month.
            <br />
            Your current streak is <strong>{consistencyStats.currentStreak} days</strong>!
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
};

// Función para calcular la racha actual de días consecutivos con entrenamiento
const calculateCurrentStreak = (workoutDates, today) => {
  if (!workoutDates || workoutDates.length === 0) return 0;
  
  // Convertir fechas a formato comparable y ordenar de más reciente a más antigua
  const sortedDates = [...workoutDates]
    .map(date => new Date(date).getTime())
    .sort((a, b) => b - a);
  
  const todayTime = today.getTime();
  const todayDate = new Date(today.getFullYear(), today.getMonth(), today.getDate()).getTime();
  
  // Verificar si hay entrenamiento hoy
  const hasWorkoutToday = sortedDates.some(date => {
    const workoutDate = new Date(date);
    return workoutDate.getFullYear() === today.getFullYear() &&
           workoutDate.getMonth() === today.getMonth() &&
           workoutDate.getDate() === today.getDate();
  });
  
  let streak = hasWorkoutToday ? 1 : 0;
  let currentDate = hasWorkoutToday ? todayDate : new Date(todayTime - 86400000).getTime();

  // Helper function to check if a specific date has workout
  const hasWorkoutOnDate = (dateToCheck) => {
    return sortedDates.some(date => {
      const workoutDate = new Date(date);
      const compareDate = new Date(dateToCheck);
      return workoutDate.getFullYear() === compareDate.getFullYear() &&
             workoutDate.getMonth() === compareDate.getMonth() &&
             workoutDate.getDate() === compareDate.getDate();
    });
  };

  // Recorrer fechas anteriores para calcular la racha
  while (true) {
    if (!hasWorkoutOnDate(currentDate)) break;

    streak++;
    currentDate = new Date(currentDate - 86400000).getTime();
  }

  return streak;
};

const WorkoutStats = () => {
  const theme = useTheme();
  const [activeGraphTab, setActiveGraphTab] = useState(0);
  const [activeCategoryTab, setActiveCategoryTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeContent, setActiveContent] = useState('statistics'); // 'statistics', 'exercises', 'measures', 'calendar'
  
  // Referencias para los elementos de pestaña
  const tabsContainerRef = useRef(null);
  const tabRefs = useRef([]);
  if (tabRefs.current.length === 0) {
    tabRefs.current = Array(3).fill(null).map(() => ({ current: null }));
  }
  
  // Estado para el indicador de pestañas y datos
  const [indicatorStyle, setIndicatorStyle] = useState({
    left: '6px',
    width: '74px'
  });
  const [workoutStats, setWorkoutStats] = useState({
    duration: [],
    volume: [],
    reps: []
  });
  const [workoutData, setWorkoutData] = useState([]);
  
  // Cargar datos de ejercicios del usuario
  useEffect(() => {
    const fetchWorkoutStats = async () => {
      setLoading(true);
      try {
        // Obtener datos de la última semana
        const today = new Date();
        const oneWeekAgo = new Date(today);
        oneWeekAgo.setDate(today.getDate() - 6);
        
        const startDate = oneWeekAgo.toISOString().split('T')[0];
        const endDate = today.toISOString().split('T')[0];
        
        const response = await agtfitService.getWorkoutStats(startDate, endDate);
        
        if (response.success) {
          setWorkoutData(response.data);
          processWorkoutData(response.data);
        } else {
          throw new Error(response.error || 'Failed to load workout stats');
        }
      } catch (err) {
        console.error('Error fetching workout stats:', err);
        setError('Failed to load workout statistics');
        // Usar datos de ejemplo si hay error
        generateMockData();
      } finally {
        setLoading(false);
      }
    };
    
    fetchWorkoutStats();
  }, []);
  
  // Procesar datos de ejercicios
  const processWorkoutData = (exercises) => {
    // Crear array de días de la semana
    // const days = [];
    const daysLabels = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

    const today = new Date();
    // const currentDay = today.getDay() || 7; // Convertir 0 (domingo) a 7

    // Agrupar ejercicios por fecha
    const exercisesByDate = {};
    
    exercises.forEach(exercise => {
      const date = exercise.exerciseDate;
      if (!exercisesByDate[date]) {
        exercisesByDate[date] = [];
      }
      exercisesByDate[date].push(exercise);
    });
    
    // Inicializar arrays para cada métrica
    const durationData = [];
    const volumeData = [];
    const repsData = [];
    
    // Iterar por los últimos 7 días
    for (let i = 6; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(today.getDate() - i);
      const dateStr = date.toISOString().split('T')[0];
      
      const dayOfWeek = date.getDay() || 7; // 0 es domingo, convertimos a 7
      const dayLabel = daysLabels[dayOfWeek - 1];
      
      const exercisesForDay = exercisesByDate[dateStr] || [];
      
      // Calcular métricas para este día
      const totalDuration = exercisesForDay.reduce((sum, ex) => sum + (parseInt(ex.duration) || 0), 0);
      
      const totalVolume = exercisesForDay.reduce((sum, ex) => {
        return sum + ((parseInt(ex.sets) || 0) * (parseInt(ex.reps) || 0) * (parseFloat(ex.weight) || 0));
      }, 0);
      
      const totalReps = exercisesForDay.reduce((sum, ex) => {
        return sum + ((parseInt(ex.sets) || 0) * (parseInt(ex.reps) || 0));
      }, 0);
      
      // Determinar si es el día actual
      const isToday = dateStr === today.toISOString().split('T')[0];
      
      durationData.push({
        label: dayLabel,
        value: totalDuration,
        active: isToday
      });
      
      volumeData.push({
        label: dayLabel,
        value: Math.round(totalVolume),
        active: isToday
      });
      
      repsData.push({
        label: dayLabel,
        value: totalReps,
        active: isToday
      });
    }
    
    setWorkoutStats({
      duration: durationData,
      volume: volumeData,
      reps: repsData
    });
  };
  
  // Generar datos de ejemplo si no hay datos reales
  const generateMockData = () => {
    const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    const today = new Date().getDay() || 7; // 0 es domingo, convertimos a 7
    
    const durationData = days.map((day, i) => ({
      label: day,
      value: Math.floor(Math.random() * 80) + 20,
      active: i + 1 === today
    }));
    
    const volumeData = days.map((day, i) => ({
      label: day,
      value: Math.floor(Math.random() * 200) + 50,
      active: i + 1 === today
    }));
    
    const repsData = days.map((day, i) => ({
      label: day,
      value: Math.floor(Math.random() * 250) + 50,
      active: i + 1 === today
    }));
    
    setWorkoutStats({
      duration: durationData,
      volume: volumeData,
      reps: repsData
    });
  };
  
  // Actualizar la posición del indicador cuando cambia la pestaña activa
  useEffect(() => {
    const updateIndicator = () => {
      if (tabRefs.current[activeGraphTab]?.current) {
        const tabElement = tabRefs.current[activeGraphTab].current;
        const tabsContainer = tabsContainerRef.current;
        
        if (tabElement && tabsContainer) {
          // Obtener la posición del contenedor de pestañas
          const containerRect = tabsContainer.getBoundingClientRect();
          // Obtener la posición de la pestaña activa
          const tabRect = tabElement.getBoundingClientRect();
          
          // Calcular la posición relativa de la pestaña dentro del contenedor
          const left = tabRect.left - containerRect.left;
          
          setIndicatorStyle({
            left: `${left}px`,
            width: `${tabRect.width}px`
          });
        }
      }
    };
    
    // Pequeño retraso para asegurar que los elementos estén renderizados
    const timer = setTimeout(updateIndicator, 50);
    return () => clearTimeout(timer);
  }, [activeGraphTab, tabRefs]);

  const handleGraphTabChange = (newValue) => {
    setActiveGraphTab(newValue);
  };
  
  const handleCategoryChange = (index) => {
    setActiveCategoryTab(index);
    
    // Cambiar el contenido según la categoría seleccionada
    switch(index) {
      case 0:
        setActiveContent('statistics');
        break;
      case 1:
        setActiveContent('exercises');
        break;
      case 2:
        setActiveContent('measures');
        break;
      case 3:
        setActiveContent('calendar');
        break;
      default:
        setActiveContent('statistics');
    }
  };

  // Obtener datos para el gráfico actual
  const getCurrentGraphData = () => {
    switch (activeGraphTab) {
      case 0: return workoutStats.duration;
      case 1: return workoutStats.volume;
      case 2: return workoutStats.reps;
      default: return workoutStats.duration;
    }
  };

  // Función para encontrar el valor máximo en los datos actuales para escalar las barras
  const getMaxValue = () => {
    const data = getCurrentGraphData();
    if (data.length === 0) return 100;
    
    return Math.max(...data.map(item => item.value)) || 100;
  };

  // Escalar los valores para el gráfico (altura máxima 100%)
  const scaleValue = (value) => {
    const maxValue = getMaxValue();
    if (maxValue === 0) return 0;
    return (value / maxValue) * 100;
  };

  // Definición de las categorías
  const categories = [
    { 
      icon: <BarChart2 size={24} />, 
      label: 'Statistics',
      description: 'View your workout stats'
    },
    { 
      icon: <Activity size={24} />, 
      label: 'Exercises',
      description: 'Browse exercises'
    },
    { 
      icon: <Ruler size={24} />, 
      label: 'Measures',
      description: 'Track your body'
    },
    { 
      icon: <Calendar size={24} />, 
      label: 'Calendar',
      description: 'Schedule workouts'
    },
  ];

  return (
    <Box sx={{ padding: theme.spacing(3, 0) }}>
      {/* Gráfico de barras */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
          Workout Progress
        </Typography>
        
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress size={40} sx={{ color: FIT_ORANGE }} />
          </Box>
        ) : error ? (
          <Typography color="error" align="center" sx={{ p: 4 }}>
            {error}
          </Typography>
        ) : (
          <BarGraph>
            {getCurrentGraphData().map((bar, index) => (
              <Box 
                key={index} 
                sx={{ 
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'flex-end', // Alinear al final para que crezca desde abajo
                  height: '100%',
                  width: '12%',
                  position: 'relative'
                }}
              >
                <Box 
                  sx={{ 
                    width: '8px', // Ancho fijo en píxeles para que sea delgada
                    height: `${scaleValue(bar.value)}%`, // Altura escalada según el valor
                    backgroundColor: bar.active ? FIT_ORANGE : theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.1)',
                    borderRadius: '4px', // Bordes redondeados
                    transition: 'height 0.5s',
                    '&:hover': {
                      backgroundColor: bar.active ? '#FF8800' : theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.15)',
                    }
                  }} 
                />
                <Typography 
                  variant="caption" 
                  sx={{ 
                    position: 'absolute',
                    bottom: -24,
                    color: bar.active ? FIT_ORANGE : theme.palette.text.secondary,
                    fontWeight: bar.active ? 600 : 400,
                  }}
                >
                  {bar.label}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    position: 'absolute',
                    bottom: `${scaleValue(bar.value) + 5}%`,
                    color: theme.palette.text.secondary,
                    fontSize: '0.65rem',
                  }}
                >
                  {bar.value > 0 ? bar.value : ''}
                </Typography>
              </Box>
            ))}
          </BarGraph>
        )}
      </Box>
      
      {/* Navigation Bar para gráficos */}
      <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
        <StyledTabs ref={tabsContainerRef}>
          <TabIndicator style={indicatorStyle} />
          <StyledTab 
            ref={tabRefs[0]}
            onClick={() => handleGraphTabChange(0)}
            selected={activeGraphTab === 0}
          >
            Duration
          </StyledTab>
          <StyledTab 
            ref={tabRefs[1]}
            onClick={() => handleGraphTabChange(1)}
            selected={activeGraphTab === 1}
          >
            Volume
          </StyledTab>
          <StyledTab 
            ref={tabRefs[2]}
            onClick={() => handleGraphTabChange(2)}
            selected={activeGraphTab === 2}
          >
            Reps
          </StyledTab>
        </StyledTabs>
      </Box>
      
      {/* Categorías de ejercicios */}
      <Box sx={{ mt: 4 }}>
        <Typography variant="h6" fontWeight={600} sx={{ mb: 3 }}>
          Workout Categories
        </Typography>
        
        <Grid container spacing={2}>
          {categories.map((category, index) => (
            <Grid item xs={6} sm={3} key={index}>
              <CategoryCard 
                active={activeCategoryTab === index}
                onClick={() => handleCategoryChange(index)}
                elevation={0}
              >
                <IconBox active={activeCategoryTab === index}>
                  {category.icon}
                </IconBox>
                <Typography variant="subtitle1" fontWeight={600} align="center">
                  {category.label}
                </Typography>
                <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 0.5 }}>
                  {category.description}
                </Typography>
              </CategoryCard>
            </Grid>
          ))}
        </Grid>
      </Box>
      
      {/* Contenido condicional según la categoría seleccionada */}
      {activeContent === 'statistics' && <StatisticsContent workoutData={workoutData} />}
      {activeContent === 'exercises' && <ExercisesContent />}
      {activeContent === 'measures' && <MeasuresContent />}
      {activeContent === 'calendar' && <CalendarContent />}
    </Box>
  );
};

export default WorkoutStats;